<script setup>
import { computed } from 'vue';
import Sharing from '../Sharing.vue';

const props = defineProps({
    post: {
        type: Object,
        required: true
    },
    isExpanded: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['toggle']);

const formattedDate = computed(() => {
    const publishedAt = new Date(props.post.published_at);
    const now = new Date();
    const diffMs = now - publishedAt;
    const diffHours = diffMs / (1000 * 60 * 60);
    const region = window.meta?.region_configuration?.region || 'en';

    if (diffHours < 12) {
        if (diffHours < 1) {
            const diffMinutes = Math.round(diffHours * 60);

            if (diffMinutes < 1) {
                return region === 'de' ? 'gerade eben' : 'just now';
            }

            if (region === 'de') {
                return `vor ${diffMinutes} Minute${diffMinutes === 1 ? '' : 'n'}   `;
            }

            return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
        }

        const hours = Math.floor(diffHours);

        if (region === 'de') {
            return `vor ${hours} Stunde${hours === 1 ? '' : 'n'}`;
        }

        return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    }

    const day = String(publishedAt.getDate()).padStart(2, '0');
    const month = String(publishedAt.getMonth() + 1).padStart(2, '0');
    const year = publishedAt.getFullYear();

    return region === 'de' ? `${day}.${month}.${year}` : `${year}-${month}-${day}`;
});
</script>

<template>
    <div
        :class="['p-3 bg-white rounded-xl shadow-xl border relative', post?.postTags?.data?.some((item) => item.name === 'BreakingNews') ? 'border-blue' : 'border-gray-200']">
        <p class="font-semibold lg:text-lg">
            {{ $translate(post.title) }}
        </p>
        <div class="flex flex-col lg:flex-row gap-2 lg:items-center justify-between text-sm mt-1">
            <div class="flex flex-row gap-2 items-center text-sm lg:text-base">
                <div class="inline-flex gap-2">
                    <p class="font-semibold">{{ formattedDate }}</p>
                </div>
                <div class="w-2 h-2 bg-blue rounded-full"></div>
                <p v-if="post.postTags && post.postTags.data.length" class="font-semibold space-x-2 line-clamp-1">
                    <span v-for="tags in post.postTags.data.slice(0, 5)" :key="tags.id"> {{ $translate(tags.name) }}
                    </span>
                </p>
            </div>
            <div class="text-center lg:text-right flex flex-row gap-3">
                <Sharing title="" url="" />

                <button v-if="post?.postTags?.data?.some((item) => item.name === 'BreakingNews')"
                    class="px-2 p-1 border border-blue text-blue hover:bg-blue hover:text-white rounded-md transition-all ease-in-out"
                    @click="$emit('toggle')">
                    {{ isExpanded ? $t('read_less') : $t('read_more') }}
                </button>
                <router-link v-else :to="props.post?.slug?.name"
                    class="px-2 p-1 inline-flex items-center border border-blue text-blue hover:bg-blue hover:text-white rounded-md transition-all ease-in-out"
                    @click="$emit('toggle')">
                    <span>
                        {{ $t('read_article', 'Artikel lesen') }}
                    </span>
                </router-link>
            </div>
        </div>

        <router-link v-if="post?.product?.image" :to="`/produkte/${post?.productGroupSlug?.name}`"
            class="absolute -left-[3rem] w-11 h-11 border border-blue rounded-full -translate-y-1/2 top-1/2 bg-white">
            <img :src="post.product.image" class="w-full h-full" />
        </router-link>
        <div v-else class="absolute -left-9 w-4 h-4 bg-blue border border-blue rounded-full -translate-y-1/2 top-1/2">
        </div>

        <TransitionExpand>
            <div v-show="isExpanded" class="mt-4 text-gray-700">
                <p v-if="post.teaser">{{ $translate(post.teaser) }}</p>
                <p v-else>No additional content available for this post.</p>
            </div>
        </TransitionExpand>
    </div>
</template>
